import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Pencil, Eye, LayoutDashboard, X, ExternalLink } from 'lucide-react';

/**
 * Floating admin toolbar (bottom-right). Only visible when a valid admin token
 * exists in localStorage. Toggles "Edit Mode" which enables/disables the
 * <EditRegion> highlight overlays across the site.
 *
 * Communicates with EditRegion via a global CSS class on <body>: `yekuv-edit-on`.
 */
export default function AdminEditBar() {
  const location = useLocation();
  const isPublic = !location.pathname.startsWith('/admin');
  const [isAdmin, setIsAdmin] = useState(false);
  const [editMode, setEditMode] = useState(() => localStorage.getItem('yekuv_edit_mode') === '1');
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    // Detect admin token; recheck on route change (e.g., after login)
    const check = () => setIsAdmin(!!localStorage.getItem('yekuv_token'));
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('yekuv-edit-on', isAdmin && editMode);
    return () => document.body.classList.remove('yekuv-edit-on');
  }, [isAdmin, editMode]);

  const toggle = () => {
    const next = !editMode;
    setEditMode(next);
    localStorage.setItem('yekuv_edit_mode', next ? '1' : '0');
  };

  if (!isAdmin || !isPublic) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[100] print:hidden" data-testid="admin-edit-bar">
      {collapsed ? (
        <button
          onClick={() => setCollapsed(false)}
          className="w-12 h-12 rounded-full bg-ink-600 text-cream shadow-2xl grid place-items-center hover:bg-ink-500"
          title="Admin araç çubuğunu aç"
        >
          <LayoutDashboard className="w-5 h-5" />
        </button>
      ) : (
        <div className="bg-ink-600 text-cream rounded-2xl shadow-2xl overflow-hidden flex items-stretch border border-gold/20">
          <button
            onClick={toggle}
            data-testid="edit-mode-toggle"
            className={`h-11 px-4 inline-flex items-center gap-2 text-sm font-semibold transition-colors ${editMode ? 'bg-gold text-ink-700' : 'hover:bg-ink-500'}`}
            title={editMode ? 'Düzenleme modu açık — kapatmak için tıklayın' : 'Düzenleme modunu aç'}
          >
            {editMode ? <><Pencil className="w-4 h-4" /> Düzenleniyor</> : <><Eye className="w-4 h-4" /> Düzenle</>}
          </button>
          <Link
            to="/admin"
            className="h-11 px-3 inline-flex items-center gap-1.5 text-xs text-cream/80 hover:text-cream border-l border-ink-500 hover:bg-ink-500"
            title="Admin paneli"
          >
            <LayoutDashboard className="w-4 h-4" /> Panel
          </Link>
          <button
            onClick={() => setCollapsed(true)}
            className="h-11 w-9 grid place-items-center text-cream/50 hover:text-cream border-l border-ink-500 hover:bg-ink-500"
            title="Küçült"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {editMode && !collapsed && (
        <div className="mt-2 bg-gold/95 text-ink-700 text-[11px] font-semibold px-3 py-1.5 rounded-full shadow-lg text-center max-w-[180px] ml-auto">
          Bölümlere gel — 'Düzenle' tıkla
        </div>
      )}
    </div>
  );
}


/**
 * Wraps a public section. When admin edit mode is on, it shows a subtle outline
 * on hover and a floating "Düzenle" button linking to the appropriate admin page.
 */
export function EditRegion({ label, to, children, className = '' }) {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    setIsAdmin(!!localStorage.getItem('yekuv_token'));
    const onStorage = () => setIsAdmin(!!localStorage.getItem('yekuv_token'));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // If no admin at all, just render the children plain. Cheap fast-path.
  if (!isAdmin) return children;

  return (
    <div className={`yekuv-edit-region ${className}`} data-edit-label={label}>
      {children}
      <Link
        to={to}
        className="yekuv-edit-btn"
        onClick={(e) => e.stopPropagation()}
        data-testid={`edit-region-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      >
        <Pencil className="w-3.5 h-3.5" />
        <span>{label}</span>
        <ExternalLink className="w-3 h-3 opacity-60" />
      </Link>
    </div>
  );
}
